import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios, { AxiosError } from 'axios';
import { Alert, Col, Form, FormGroup, FormText, Input, Label, Row, Spinner } from 'reactstrap';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useAppDispatch, useAppSelector } from 'hooks';

import { apiDexExport, apiDexExportCsv } from '~constants/api';
import { getErrorMessage, serializeParams } from '~libs/axiosWrapper';

import { Button, ContainerHeader, ErrorResponse } from '~components';
import { DateInput, MultiSelect } from '~components/form-fields/inputs';
import { Stepper } from '~components/wizard-form';
import { setTitle } from '~main/actions/login';
import { getAll as getClientPackages } from '~main/actions/clients/clientPackages';
import { selectLookupClientPackages } from '~main/selectors/clients/clientPackages';
import { getAll as getServiceTypes } from '~appointments/actions/serviceTypes';
import { selectAppointmentServiceTypes } from '~appointments/selectors/serviceTypes';

import { useLazyGetDexPreviewQuery } from '../actions/dex';
import { DexExportParams } from '../types';
import DexPreviewPanel from './DexPreviewPanel';

type Step = 'configure' | 'preview';

interface FormValues {
  date_from: string;
  date_to: string;
  de_identify: boolean;
  package_id: number[];
  service_type_id: number[];
}

const INITIAL_VALUES: FormValues = {
  date_from: '',
  date_to: '',
  de_identify: false,
  package_id: [],
  service_type_id: [],
};

// These reason strings are hand-duplicated from the backend. There is no shared source of truth
// and no contract test tying the two sides together, so a reword on the API side silently breaks
// this classification. They are pushed by preview() in tp-assist-api/src/libs/dex_export.mjs: the
// advisory-only reasons at line 460 ('Missing DEX client ID') and line 491 (the de-identified SLK
// warning), and the excluding reasons at line 471 (no DEX case ID), line 511 (no DEX code mapping)
// and line 512 (client excluded from DEX export).
//
// Note which way the unmatched-reason fallback fails. An unrecognised reason is treated as
// advisory, so a backend reword UNDERSTATES exclusions: appointments that really are missing from
// the XML would be reported as "included", and the banner that exists to stop users filing an
// incomplete DSS submission would stop warning about them. For a DSS submission that is the
// dangerous direction, not a safe default. It is a known gap, accepted for now.

// Excluded because DEX configuration is missing or unmapped. A real, fixable gap, so this group
// keeps a call to action.
const CONFIG_GAP_DEX_ISSUE_REASONS = new Set([
  'No DEX case ID - package missing or not configured',
  'Service type has no DEX code mapping',
]);

// Excluded on purpose: the client-level exclude_from_dex flag (tp-v1's clientExcludeFromDEX), set
// by an operator so that client is never reported to DSS. Nothing is broken, so this group gets a
// neutral, factual sentence and no call to action.
const EXCLUDED_CLIENT_DEX_ISSUE_REASON = 'Client excluded from DEX export';

// Both date fields carry the same range check, so either edit order surfaces the error on the
// field the user actually touched. The two wordings are kept distinct so each reads naturally
// under its own field - only one of them is ever rendered, see showFromError below.
const RANGE_ERROR_FROM = 'From date must be before to date';
const RANGE_ERROR_TO = 'To date must be after from date';

const validationSchema = Yup.object({
  date_from: Yup.string()
    .required('From date is required')
    .test('date-range', RANGE_ERROR_FROM, function (date_from) {
      const { date_to } = this.parent as FormValues;
      return !date_from || !date_to || date_from <= date_to;
    }),
  date_to: Yup.string()
    .required('To date is required')
    .test('date-range', RANGE_ERROR_TO, function (date_to) {
      const { date_from } = this.parent as FormValues;
      return !date_from || !date_to || date_from <= date_to;
    }),
});

const DexExportPage: React.FC = () => {
  useEffect(() => {
    setTitle('DEX Export');
  }, []);

  const dispatch = useAppDispatch();
  const clientPackages = useAppSelector(selectLookupClientPackages);
  const rawServiceTypes = useAppSelector(selectAppointmentServiceTypes);
  const dexServiceTypes = useMemo(() => rawServiceTypes.filter((s) => s.dex_code), [rawServiceTypes]);

  useEffect(() => {
    dispatch(getClientPackages({ sort: ['name'], deleted: false }));
    dispatch(getServiceTypes());
  }, [dispatch]);

  const [step, setStep] = useState<Step>('configure');
  const [isDownloading, setIsDownloading] = useState(false);
  const [isDownloadingCsv, setIsDownloadingCsv] = useState(false);
  // Only one download outcome is ever shown at a time - starting either download, or leaving the
  // preview step, clears whatever banner (from either format) was previously shown.
  const [downloadStatus, setDownloadStatus] = useState<
    { format: 'xml' | 'csv'; state: 'success' } | { format: 'xml' | 'csv'; state: 'error'; message: string } | null
  >(null);
  // Bumped on every new download and on Previous, so a request whose response arrives after it's
  // been superseded (Previous clicked, or a later download started) can tell it's stale and skip
  // writing downloadStatus - otherwise a slow request resolving late could paint a banner for a
  // preview session (or a different format) the user has already moved on from.
  const downloadRequestId = useRef(0);

  const [triggerPreview, { data: preview, isFetching, error: previewError }] = useLazyGetDexPreviewQuery();

  const buildParams = (values: FormValues): DexExportParams => ({
    date_from: values.date_from,
    date_to: values.date_to,
    ...(values.de_identify ? { de_identify: true } : {}),
    ...(values.package_id.length ? { package_id: values.package_id } : {}),
    ...(values.service_type_id.length ? { service_type_id: values.service_type_id } : {}),
  });

  const formik = useFormik<FormValues>({
    initialValues: INITIAL_VALUES,
    validationSchema,
    onSubmit: async (values) => {
      try {
        await triggerPreview(buildParams(values)).unwrap();
        setStep('preview');
      } catch {
        // previewError state surfaces the message below the form
      }
    },
  });

  const handleDateChange = (id: string, value: string | null) => {
    formik.setFieldValue(id, value ?? '');
    formik.setFieldTouched(id, true, false);
  };

  // An invalid range fails the range check on both fields at once, so without this the single
  // mistake would render two differently-worded messages in adjacent columns. Show it once:
  // under To normally, falling back to From when To was never touched. Required-field messages
  // are unaffected - the range check short-circuits on an empty value, so an empty form still
  // flags both fields.
  const isRangeError = formik.errors.date_from === RANGE_ERROR_FROM && formik.errors.date_to === RANGE_ERROR_TO;
  const showFromError =
    !!formik.touched.date_from && !!formik.errors.date_from && !(isRangeError && !!formik.touched.date_to);
  const showToError = !!formik.touched.date_to && !!formik.errors.date_to;

  const handleBack = () => {
    setStep('configure');
    setDownloadStatus(null);
    downloadRequestId.current += 1;
  };

  // Shared by the XML and CSV downloads - both are whole files built server-side and returned as a
  // blob with a Content-Disposition filename, so only the endpoint/default filename/format differ.
  // Bumping downloadRequestId here (not just in handleBack) also means starting one format
  // supersedes an in-flight request for the other, so the banner always reflects the most recently
  // started download rather than whichever of the two happens to resolve last.
  const downloadExport = async (
    format: 'xml' | 'csv',
    url: string,
    defaultFilename: string,
    setLoading: (value: boolean) => void,
  ) => {
    const requestId = ++downloadRequestId.current;
    setLoading(true);
    setDownloadStatus(null);
    try {
      const response = await axios.get(url, {
        params: buildParams(formik.values),
        paramsSerializer: { serialize: serializeParams },
        responseType: 'blob',
      });
      const contentDisposition = response.headers['content-disposition'];
      const filenameMatch = contentDisposition?.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
      const filename = filenameMatch ? decodeURIComponent(filenameMatch[1]) : defaultFilename;
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(new Blob([response.data]));
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(link.href);
      if (downloadRequestId.current === requestId) setDownloadStatus({ format, state: 'success' });
    } catch (err) {
      const axiosErr = err as AxiosError;
      let message = 'Download failed. Please try again.';
      const data = axiosErr.response?.data;
      if (data instanceof Blob) {
        try {
          message = getErrorMessage(JSON.parse(await data.text()));
        } catch {
          // keep fallback message - error body wasn't valid JSON
        }
      } else if (data) {
        message = getErrorMessage(data);
      }
      if (downloadRequestId.current === requestId) setDownloadStatus({ format, state: 'error', message });
    } finally {
      setLoading(false);
    }
  };

  const isAnyDownloading = isDownloading || isDownloadingCsv;

  const handleDownload = () => downloadExport('xml', apiDexExport, 'dex-export.xml', setIsDownloading);

  const handleDownloadCsv = () => downloadExport('csv', apiDexExportCsv, 'dex-export.csv', setIsDownloadingCsv);

  const readinessUrl = `/dex/readiness?date_from=${encodeURIComponent(formik.values.date_from)}&date_to=${encodeURIComponent(formik.values.date_to)}`;

  // These counts describe APPOINTMENTS, not issue rows. preview() can push several issues for the
  // same appointment_id (an appointment with no client_dexid gets 'Missing DEX client ID' and then,
  // if resolveCaseId() also fails, 'No DEX case ID'), so counting rows both double-counted an
  // appointment and let a single appointment be described as excluded and included in the same
  // banner. Each appointment now lands in exactly one bucket, config-gap winning over
  // excluded-client because it is the bucket that carries an action.
  const { configGapAppointmentCount, excludedClientAppointmentCount, flaggedAppointmentCount } = useMemo(() => {
    const reasonsByAppointment = new Map<number, string[]>();
    (preview?.issues ?? []).forEach((issue) => {
      const reasons = reasonsByAppointment.get(issue.appointment_id);
      if (reasons) {
        reasons.push(issue.reason);
      } else {
        reasonsByAppointment.set(issue.appointment_id, [issue.reason]);
      }
    });

    let configGap = 0;
    let excludedClient = 0;
    let flagged = 0;
    reasonsByAppointment.forEach((reasons) => {
      if (reasons.some((reason) => CONFIG_GAP_DEX_ISSUE_REASONS.has(reason))) {
        configGap += 1;
      } else if (reasons.includes(EXCLUDED_CLIENT_DEX_ISSUE_REASON)) {
        excludedClient += 1;
      } else {
        flagged += 1;
      }
    });

    return {
      configGapAppointmentCount: configGap,
      excludedClientAppointmentCount: excludedClient,
      flaggedAppointmentCount: flagged,
    };
  }, [preview?.issues]);

  // DexPreviewPanel's "Data quality issues (n)" tile counts issue rows, so it can be higher than
  // the number of appointments named here. Say so when the two genuinely differ, rather than
  // leaving it looking like one of the two numbers is wrong.
  const issueAppointmentCount = configGapAppointmentCount + excludedClientAppointmentCount + flaggedAppointmentCount;

  // The count sentences are a list rather than consecutive paragraphs, and each count is emphasised,
  // because the excluded/included split is the whole point of the banner - this ticket exists because
  // users read the previous copy and believed excluded appointments had been submitted. Run together
  // as prose the operative numbers have to be parsed out of a four-line block, which only half-solves
  // that. The trailing notes stay as prose below the list: they qualify the counts rather than adding
  // another bucket to compare.
  const issuesBanner =
    preview && preview.issues.length > 0 ? (
      <Alert color="warning" className="mt-3 mb-0" data-cy="dex-issues-cta">
        <ul className="mb-0 ps-3">
          {configGapAppointmentCount > 0 && (
            <li>
              <strong>{configGapAppointmentCount}</strong> appointment
              {configGapAppointmentCount !== 1 ? 's are' : ' is'} excluded from this export due to missing or unmapped
              DEX configuration. Resolve these issues before exporting to avoid an incomplete submission.
            </li>
          )}
          {excludedClientAppointmentCount > 0 && (
            <li>
              <strong>{excludedClientAppointmentCount}</strong> appointment
              {excludedClientAppointmentCount !== 1 ? 's are' : ' is'} excluded from this export because the client has
              been set to be excluded from DEX reporting.
            </li>
          )}
          {flaggedAppointmentCount > 0 && (
            <li>
              <strong>{flaggedAppointmentCount}</strong> appointment{flaggedAppointmentCount !== 1 ? 's have' : ' has'}{' '}
              data quality issues but {flaggedAppointmentCount !== 1 ? 'remain' : 'remains'} included in the export.
            </li>
          )}
        </ul>
        {issueAppointmentCount !== preview.issues.length && (
          <div className="mt-2">
            These counts are per appointment, not per issue, so an appointment with more than one issue is counted once
            here.
          </div>
        )}
        <div className="mt-2">
          Missing Stage 3 session data can be reviewed in the{' '}
          <a href={readinessUrl} target="_blank" rel="noopener noreferrer">
            DEX Readiness Report
          </a>
          .
        </div>
      </Alert>
    ) : null;

  return (
    <div className="dex-export-page p-2" data-cy="dex-export-page">
      <ContainerHeader dataCy="dex-export-header">DEX Export (Beta)</ContainerHeader>

      <div className="wizard-form">
        <Stepper
          currentStep={step === 'configure' ? 0 : 1}
          steps={[
            { title: 'Configure', step: 0, fields: {} },
            { title: 'Preview', step: 1, fields: {} },
          ]}
        />

        <div className="d-flex flex-column flex-grow-1">
          <div className="wizard-form-content">
            {step === 'configure' && (
              <>
                <div className="py-2">
                  <p className="text-muted mb-3">
                    Select a date range and download the DEX XML file for submission to DSS, or a CSV copy of the
                    same data for internal record-keeping.
                  </p>

                  <Form onSubmit={formik.handleSubmit}>
                    <Row>
                      <Col md={3}>
                        <FormGroup>
                          <Label for="date_from">From Date</Label>
                          <DateInput
                            type="date"
                            id="date_from"
                            name="date_from"
                            value={formik.values.date_from}
                            onChange={handleDateChange}
                            dataCy="dex-from-date"
                          />
                          {showFromError && (
                            <div className="invalid-feedback d-block" data-cy="dex-from-date-error">
                              {formik.errors.date_from}
                            </div>
                          )}
                        </FormGroup>
                      </Col>
                      <Col md={3}>
                        <FormGroup>
                          <Label for="date_to">To Date</Label>
                          <DateInput
                            type="date"
                            id="date_to"
                            name="date_to"
                            value={formik.values.date_to}
                            onChange={handleDateChange}
                            dataCy="dex-to-date"
                          />
                          {showToError && (
                            <div className="invalid-feedback d-block" data-cy="dex-to-date-error">
                              {formik.errors.date_to}
                            </div>
                          )}
                        </FormGroup>
                      </Col>
                      <Col md={6}>
                        {/* Sits in the same row as the dates. The label lines up with the date labels,
                            and the checkbox stays centred on the label line (Bootstrap form-check). */}
                        <FormGroup check className="mb-3">
                          <Input
                            type="checkbox"
                            id="dex-de-identify"
                            name="de_identify"
                            className="form-check-input"
                            checked={formik.values.de_identify}
                            onChange={formik.handleChange}
                            aria-describedby="dex-de-identify-help"
                            data-cy="dex-de-identify-checkbox"
                          />
                          <Label check for="dex-de-identify">
                            De-identify export
                          </Label>
                          <FormText id="dex-de-identify-help" className="d-block" data-cy="dex-de-identify-help">
                            Omit client names and addresses, and use SLK only.
                          </FormText>
                        </FormGroup>
                      </Col>
                    </Row>

                    <Row>
                      <Col md={6}>
                        <FormGroup>
                          <Label for="package_id">Package</Label>
                          <div data-cy="dex-package-filter">
                            <MultiSelect
                              type="select"
                              id="package_id"
                              name="package_id"
                              value={formik.values.package_id}
                              options={clientPackages}
                              placeholder="All"
                              setFieldValue={formik.setFieldValue}
                            />
                          </div>
                        </FormGroup>
                      </Col>
                      <Col md={6}>
                        <FormGroup>
                          <Label for="service_type_id">Service Type</Label>
                          <div data-cy="dex-service-type-filter">
                            <MultiSelect
                              type="select"
                              id="service_type_id"
                              name="service_type_id"
                              value={formik.values.service_type_id}
                              options={dexServiceTypes}
                              placeholder="All"
                              setFieldValue={formik.setFieldValue}
                              allowSelectAll
                            />
                          </div>
                        </FormGroup>
                      </Col>
                    </Row>
                  </Form>

                  {previewError && (
                    <ErrorResponse message={getErrorMessage(previewError)} className="mt-3" dataCy="dex-preview-error" />
                  )}
                </div>

                <div className="d-flex justify-content-end mt-auto pt-3">
                  <Button
                    color="success"
                    onClick={() => formik.handleSubmit()}
                    disabled={isFetching}
                    data-cy="dex-preview-button"
                  >
                    {isFetching ? <Spinner size="sm" /> : 'Preview'}
                  </Button>
                </div>
              </>
            )}

            {step === 'preview' && preview && (
              <>
                <div className="py-2">
                  {downloadStatus?.state === 'error' && (
                    <ErrorResponse
                      message={downloadStatus.message}
                      className="mb-3"
                      dataCy={downloadStatus.format === 'xml' ? 'dex-download-error' : 'dex-download-csv-error'}
                    />
                  )}

                  {downloadStatus?.state === 'success' && (
                    <Alert
                      color="success"
                      className="mb-3"
                      data-cy={downloadStatus.format === 'xml' ? 'dex-download-success' : 'dex-download-csv-success'}
                    >
                      {downloadStatus.format === 'xml' ? 'XML download complete.' : 'CSV download complete.'}
                    </Alert>
                  )}

                  <DexPreviewPanel preview={preview} issuesBanner={issuesBanner} data-cy="dex-preview-panel" />

                  {formik.values.de_identify && (
                    <Alert color="info" className="mt-3" data-cy="dex-csv-de-identify-note">
                      Download CSV is disabled while de-identify is selected. The CSV export always includes real
                      client names and addresses for internal record-keeping, and does not support
                      de-identification the way the XML export does.
                    </Alert>
                  )}
                </div>

                <div className="d-flex justify-content-end mt-auto pt-3">
                  <Button color="secondary" className="me-2" onClick={handleBack} data-cy="dex-back-button">
                    Previous
                  </Button>
                  <Button
                    color="success"
                    className="me-2"
                    onClick={handleDownloadCsv}
                    disabled={isAnyDownloading || formik.values.de_identify}
                    title={
                      formik.values.de_identify
                        ? 'CSV always includes real client names and addresses, and does not support de-identification.'
                        : undefined
                    }
                    data-cy="dex-download-csv-button"
                  >
                    {isDownloadingCsv ? <Spinner size="sm" /> : 'Download CSV'}
                  </Button>
                  <Button
                    color="success"
                    onClick={handleDownload}
                    disabled={isAnyDownloading}
                    data-cy="dex-download-button"
                  >
                    {isDownloading ? <Spinner size="sm" /> : 'Download XML'}
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DexExportPage;
